import { useParams, useNavigate } from "react-router-dom";
import { useVolunteerData } from "../hooks/useVolunteerData";
import styles from "./VolunteerDetail.module.scss";
import { Button, CircularProgress, Alert } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import StarIcon from "@mui/icons-material/Star";
import { SkillsInterestsList } from "../components/SkillsInterestsList/SkillsInterestsList";
import { useTranslation } from "../i18n";
import { localeTag } from "../i18n/dateLocale";

export const VolunteerDetail = () => {
  const { volunteerId } = useParams<{ volunteerId: string }>();
  const navigate = useNavigate();
  const { data, loading, error } = useVolunteerData(volunteerId!);
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <CircularProgress />
          <p>{t("volunteer.loadingProfile")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Alert severity="error" className={styles.error}>
            {t(error)}
          </Alert>
          <Button onClick={() => navigate(-1)} variant="outlined">
          {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Alert severity="warning">{t("volunteer.notFound")}</Alert>
          <Button onClick={() => navigate(-1)} variant="outlined">
          {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  const profileImage = 'profile_image' in data ? data.profile_image : undefined;
  const email = 'email' in data ? data.email : undefined;
  const dateOfBirth = 'date_of_birth' in data ? data.date_of_birth : undefined;
  const linkedinLink = 'linkedin_link' in data ? data.linkedin_link : undefined;
  const volunteerRating = 'volunteer_rating' in data ? data.volunteer_rating : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          {profileImage ? (
            <img src={profileImage as string} alt={`${data.first_name} ${data.last_name}`} />
          ) : (
            <PersonIcon sx={{ fontSize: 80 }} />
          )}
        </div>
        <div className={styles.headerInfo}>
          <h1>
            {data.first_name} {data.last_name}
          </h1>
          {email && (
            <div className={styles.emailHeader}>
              <EmailIcon fontSize="small" />
              <span>{email}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {data.skills && data.skills.length > 0 && (
          <section className={styles.section || ''}>
            <SkillsInterestsList 
              items={data.skills} 
              type="skills" 
              title={t("volunteer.skills")} 
            />
          </section>
        )}

        {data.interests && data.interests.length > 0 && (
          <section className={styles.section || ''}>
            <SkillsInterestsList 
              items={data.interests} 
              type="interests" 
              title={t("volunteer.interests")} 
            />
          </section>
        )}

        {(dateOfBirth || linkedinLink || volunteerRating !== undefined) && (
          <section className={styles.section}>
            <h2>{t("volunteer.otherInfo")}</h2>
            <div className={styles.infoGrid}>
              {dateOfBirth && (
                <div className={styles.infoItem}>
                  <PersonIcon />
                  <span>
                    {t("volunteer.dobInline", {
                      date: new Date(dateOfBirth).toLocaleDateString(localeTag()),
                    })}
                  </span>
                </div>
              )}
              {linkedinLink && (
                <div className={styles.infoItem}>
                  <LinkedInIcon />
                  <a href={linkedinLink} target="_blank" rel="noopener noreferrer">
                    {t("volunteer.linkedinProfile")}
                  </a>
                </div>
              )}
              {volunteerRating !== undefined && volunteerRating !== null && (
                <div className={styles.infoItem}>
                  <StarIcon />
                  <span>
                    {t("volunteer.ratingInline", {
                      rating: volunteerRating.toFixed(1),
                    })}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      <div className={styles.footer}>
        <Button onClick={() => navigate(-1)} variant="outlined">
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
};

